import type { Language, PikbSubmission } from "../types";

export const pikbMinimumSummaryWords = 10;

export const pikbDefaultSubmission: PikbSubmission = {
  zone: "",
  category: "",
  leader_name: "",
  leader_ic: "",
  leader_phone: "",
  leader_address: "",
  members: [],
  innovation_title: "",
  innovation_summary: "",
};

export const pikbCopy = {
  appName: {
    ms: "Borang PIKB 2026",
    en: "PIKB 2026 Entry Form",
  },
  shortName: {
    ms: "PIKB 2026",
    en: "PIKB 2026",
  },
  subtitle: {
    ms: "Pertandingan inovasi berasaskan kearifan lokal, pengetahuan tradisional dan sumber tempatan masyarakat Borneo di semua zon penganjuran.",
    en: "An innovation competition based on Borneo local wisdom, traditional knowledge and community resources across all host zones.",
  },
  survey: {
    ms: "Borang PIKB",
    en: "PIKB Form",
  },
  start: {
    ms: "Buka Borang",
    en: "Open Form",
  },
  continue: {
    ms: "Mula Isi Borang",
    en: "Start Form",
  },
  next: {
    ms: "Seterusnya",
    en: "Next",
  },
  back: {
    ms: "Kembali",
    en: "Back",
  },
  submit: {
    ms: "Hantar Penyertaan",
    en: "Submit Entry",
  },
  submitting: {
    ms: "Menghantar...",
    en: "Submitting...",
  },
  required: {
    ms: "Sila lengkapkan maklumat ini.",
    en: "Please complete this information.",
  },
  minimumWords: {
    ms: `Minimum ${pikbMinimumSummaryWords} perkataan diperlukan.`,
    en: `Minimum ${pikbMinimumSummaryWords} words are required.`,
  },
  review: {
    ms: "Semak maklumat penyertaan sebelum dihantar.",
    en: "Review the entry before submitting.",
  },
  addMember: {
    ms: "Tambah Ahli",
    en: "Add Member",
  },
  removeMember: {
    ms: "Buang Ahli",
    en: "Remove Member",
  },
  noMembers: {
    ms: "Tiada ahli tambahan. Ketua kumpulan sahaja akan direkodkan.",
    en: "No additional members. Only the group leader will be recorded.",
  },
  thankYou: {
    ms: "Terima kasih. Penyertaan anda telah diterima.",
    en: "Thank you. Your entry has been received.",
  },
  thankYouDetail: {
    ms: "Maklumat pertandingan akan disemak oleh pihak penganjur.",
    en: "The organizer will review the competition entry.",
  },
  another: {
    ms: "Isi Penyertaan Baru",
    en: "Submit Another Entry",
  },
  words: {
    ms: "perkataan",
    en: "words",
  },
};

export const pikbEventInfo = [
  {
    label: { ms: "Masa", en: "Time" },
    value: { ms: "7.00 am - 5.00 pm", en: "7.00 am - 5.00 pm" },
  },
  {
    label: { ms: "Tarikh tutup", en: "Closing date" },
    value: { ms: "13 Jun 2026 (Sabtu)", en: "13 June 2026 (Saturday)" },
  },
];

export const pikbZones = [
  {
    value: "beaufort",
    label: { ms: "Beaufort", en: "Beaufort" },
    venue: { ms: "Dewan Pa' Musa", en: "Dewan Pa' Musa" },
    date: { ms: "27 Jun 2026", en: "27 June 2026" },
    finaleOnly: false,
  },
  {
    value: "lahad_datu",
    label: { ms: "Lahad Datu", en: "Lahad Datu" },
    venue: { ms: "Dewan Tun Mustapha", en: "Dewan Tun Mustapha" },
    date: { ms: "11 Julai 2026", en: "11 July 2026" },
    finaleOnly: false,
  },
  {
    value: "ranau",
    label: { ms: "Ranau", en: "Ranau" },
    venue: { ms: "Dewan Masyarakat Ranau", en: "Dewan Masyarakat Ranau" },
    date: { ms: "25 Julai 2026", en: "25 July 2026" },
    finaleOnly: false,
  },
  {
    value: "kudat",
    label: { ms: "Kudat", en: "Kudat" },
    venue: { ms: "Dewan Tun Mustapha", en: "Dewan Tun Mustapha" },
    date: { ms: "8 Ogos 2026", en: "8 August 2026" },
    finaleOnly: false,
  },
  {
    value: "tuaran_kota_kinabalu",
    label: { ms: "Tuaran / Kota Kinabalu", en: "Tuaran / Kota Kinabalu" },
    venue: { ms: "Dewan Sri Sulaman", en: "Dewan Sri Sulaman" },
    date: { ms: "22 Ogos 2026", en: "22 August 2026" },
    finaleOnly: false,
  },
  {
    value: "grand_finale_ums",
    label: { ms: "Grand Finale - UMS", en: "Grand Finale - UMS" },
    venue: { ms: "Universiti Malaysia Sabah", en: "Universiti Malaysia Sabah" },
    date: { ms: "13-14 Oktober 2026", en: "13-14 October 2026" },
    finaleOnly: true,
  },
] as const;

export const pikbJoiningZones = pikbZones.filter((zone) => !zone.finaleOnly);

export const pikbRules = {
  ms: [
    "Penyertaan adalah percuma.",
    "Terbuka kepada semua lapisan masyarakat.",
    "Setiap peserta hanya dibenarkan menggunakan satu nama bagi maksimum tiga penyertaan sahaja.",
    "Meja berukuran 1.5 kaki x 2.0 kaki akan disediakan.",
  ],
  en: [
    "Participation is free.",
    "Open to all members of the community.",
    "Each participant may use one name for a maximum of three entries only.",
    "A 1.5 ft x 2.0 ft table will be provided.",
  ],
};

export const pikbCategories = [
  {
    value: "makanan_kesihatan_tradisional",
    label: {
      ms: "Makanan dan Kesihatan Tradisional",
      en: "Traditional Food and Health",
    },
    description: {
      ms: "Inovasi berasaskan ramuan, makanan, amalan dan kesejahteraan tradisional.",
      en: "Innovation based on ingredients, food, practices and traditional wellbeing.",
    },
  },
  {
    value: "keselamatan_kemakmuran_komuniti",
    label: {
      ms: "Keselamatan dan Kemakmuran Komuniti",
      en: "Community Safety and Prosperity",
    },
    description: {
      ms: "Idea yang membantu komuniti hidup lebih selamat, teratur dan sejahtera.",
      en: "Ideas that help communities live more safely, orderly and prosperously.",
    },
  },
  {
    value: "seni_kraftangan_muzik_tradisional",
    label: {
      ms: "Seni, Kraftangan dan Peralatan Muzik Tradisional",
      en: "Traditional Arts, Crafts and Musical Instruments",
    },
    description: {
      ms: "Produk atau kaedah baharu berasaskan seni, kraftangan dan muzik tradisi.",
      en: "New products or methods based on traditional arts, crafts and music.",
    },
  },
];

export const pikbSteps = [
  {
    id: "intro",
    eyebrow: { ms: "Maklumat", en: "Info" },
    title: {
      ms: "Penyertaan Pertandingan Inovasi Kearifan Borneo",
      en: "Borneo Wisdom Innovation Competition Entry",
    },
    description: {
      ms: "Sila baca maklumat ringkas pertandingan sebelum mengisi borang penyertaan.",
      en: "Please review the competition details before filling in the entry form.",
    },
  },
  {
    id: "zone",
    eyebrow: { ms: "Seksyen 1", en: "Section 1" },
    title: { ms: "Pilihan Zon", en: "Zone Selection" },
    description: {
      ms: "Pilih zon penganjuran yang anda ingin sertai.",
      en: "Choose the host zone you want to join.",
    },
  },
  {
    id: "category",
    eyebrow: { ms: "Seksyen 2", en: "Section 2" },
    title: { ms: "Pilihan Kategori", en: "Category Selection" },
    description: {
      ms: "Pilih satu kategori yang paling sesuai dengan inovasi anda.",
      en: "Choose one category that best fits your innovation.",
    },
  },
  {
    id: "leader",
    eyebrow: { ms: "Seksyen 3", en: "Section 3" },
    title: { ms: "Nama Ketua Kumpulan", en: "Group Leader" },
    description: {
      ms: "Lengkapkan maklumat ketua kumpulan dan tambah ahli jika ada.",
      en: "Complete the group leader details and add members if any.",
    },
  },
  {
    id: "innovation",
    eyebrow: { ms: "Seksyen 4", en: "Section 4" },
    title: { ms: "Maklumat Inovasi", en: "Innovation Details" },
    description: {
      ms: `Nyatakan tajuk inovasi dan penjelasan ringkas minimum ${pikbMinimumSummaryWords} patah perkataan.`,
      en: `Provide the innovation title and a short explanation of at least ${pikbMinimumSummaryWords} words.`,
    },
  },
  {
    id: "review",
    eyebrow: { ms: "Semakan", en: "Review" },
    title: { ms: "Semak Sebelum Hantar", en: "Review Before Submit" },
    description: {
      ms: "Pastikan semua maklumat penyertaan adalah betul.",
      en: "Make sure all entry details are correct.",
    },
  },
] as const;

export type PikbStepId = (typeof pikbSteps)[number]["id"];

export function getPikbCategoryLabel(value: string, language: Language) {
  return (
    pikbCategories.find((category) => category.value === value)?.label[
      language
    ] ?? value
  );
}

export function getPikbZoneLabel(value: string, language: Language) {
  return (
    pikbZones.find((zone) => zone.value === value)?.label[language] ?? value
  );
}

export function getWordCount(value: string) {
  return value.trim() ? value.trim().split(/\s+/).length : 0;
}
